import React, { ChangeEvent, MouseEventHandler, useCallback, useMemo, useRef } from "react";
import { FaPaperclip } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import styles from "./upload-files.module.scss";
import classnames from "classnames";
import { calFileListSizeV2, calFileSizeV2 } from "@whatslly/commons/utils";
import { Text } from "@whatslly/ui-components/Text";
import UITypes from "@whatslly/types/UI-V2/UITypes";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";

type UploadFilesBaseProps = {
  multiple?: boolean;
  accept?: string;
  className?: string;
  onChange: (files: File[]) => void;
  darkMode?: boolean;
};
/**
 * @deprecated TODO create a pr to remove dark mode from all <Text/> in the app
 */
export type UploadFilesProps = UploadFilesBaseProps & {
  showClear?: boolean;
  onClear?: (fileName?: string) => void;
  files: File[] | undefined;
  paddingInline?: UITypes["paddingInlineType"];
};

export const UploadFilesV2: React.FC<UploadFilesProps> = ({
  files,
  onChange,
  className,
  multiple = false,
  accept = ".txt, .csv, .doc, .docx, .pdf, .txt, .xls, .xlsx, .ppt, .pptx, .png, .jpeg, .jpg, .gif, .ogg, .mp3",
  showClear,
  onClear,
  darkMode,
}) => {
  const { t } = useTranslation();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const onFilesChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) {
        return;
      }
      onChange(Array.from(files));
    },
    [onChange]
  );

  const selectedFilesTrashText = useMemo(() => {
    if (!showClear || !files) {
      return;
    }

    const size = calFileListSizeV2(files);
    return !size
      ? `${t("file_size_limit")} (>10MB)!`
      : files.length === 1
        ? `${t("selected_one_file")} (${size})`
        : `${t("selected")} ${files.length} ${t("files")} (${size})`;
  }, [files, showClear, t]);

  const onClickOnTrash = useCallback(
    (file: File) => {
      if (onClear) {
        onClear(file.name);
      }
      if (hiddenFileInput.current) {
        hiddenFileInput.current.value = "";
      }
    },
    [onClear]
  );

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    hiddenFileInput.current?.click();
  };

  return (
    <div className={styles.uploadFilesContainer}>
      <div className={classnames(styles.uploadFiles, className, { [styles.darkMode]: darkMode })}>
        <button
          type="button"
          onClick={handleClick}
          className={classnames(
            globalStyles["bg-color-neutral-tertiary"],
            globalStyles["hover-bg-color-neutral-secondary"],
            globalStyles["color-main-primary"]
          )}
        >
          <FaPaperclip className={classnames(globalStyles["color-main-primary"])} />
          {t("attach_files")}
        </button>
        <input ref={hiddenFileInput} onChange={onFilesChanged} type="file" accept={accept} multiple={multiple} />
      </div>
      {files &&
        files.map(
          (file) =>
            showClear &&
            selectedFilesTrashText && (
              <div
                className={classnames(
                  styles.clearAndText,
                  globalStyles["bg-color-secondary-primary"],
                  globalStyles["bc-neutral-secondary"],
                  globalStyles["color-main-primary"],
                  {
                    [styles.darkMode]: darkMode,
                  }
                )}
                key={file.name}
              >
                <div className={styles.textContainer}>
                  <Text className={styles.fileNameText}>{file.name}</Text>
                  <p className={classnames(styles.sizeText, globalStyles["color-neutral-primary"])}>({calFileSizeV2(file)})</p>
                </div>
                <div className={styles.clearIconContainer} onClick={() => onClickOnTrash(file)}>
                  <Text className={classnames(styles.clearIcon, globalStyles["color-main-primary"])}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" />
                    </svg>
                  </Text>
                </div>
              </div>
            )
        )}
    </div>
  );
};
